import { useState, useCallback, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Flex, Text, Grid, SimpleGrid } from '@chakra-ui/react';
import { Dialog } from '@chakra-ui/react';
import { useInterval } from '@/hooks/useInterval';
import { api, ApiError } from '@/lib/api';
import { useToast } from '@/components/ui/Toast';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Badge, RecommendationBadge, RiskBadge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { PriceDisplay } from '@/components/ui/PriceDisplay';
import { Skeleton } from '@/components/ui/Skeleton';
import { CandlestickChart } from '@/components/charts/CandlestickChart';
import { TradeOffLens } from '@/components/TradeOffLens';
import { ExplainDialog } from '@/components/ExplainDialog';
import { useExplain } from '@/hooks/useExplain';
import { CADENCE, isMarketHours, getExchangeHours } from '@market-buddy/shared';
import type {
  InstrumentSnapshotResponse,
  DecisionResponse,
  IndicatorsResponse,
  CandlesResponse,
  MaterialChange,
  StrategistOutput,
  StrategistResponse,
  RiskDriver,
} from '@market-buddy/shared';

export function Cockpit() {
  const { symbol: rawSymbol } = useParams<{ symbol: string }>();
  const symbol = rawSymbol?.toUpperCase();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [snapshot, setSnapshot] = useState<InstrumentSnapshotResponse | null>(null);
  // The snapshot 404s when the cached price has expired (e.g. over a weekend —
  // `price:{symbol}` KV has a 24h TTL). Track it so the header degrades to an
  // honest "no recent data" message instead of skeletons that never resolve.
  const [snapshotUnavailable, setSnapshotUnavailable] = useState(false);
  const [decision, setDecision] = useState<DecisionResponse | null>(null);
  const [indicators, setIndicators] = useState<IndicatorsResponse | null>(null);
  const [candles, setCandles] = useState<CandlesResponse['candles']>([]);
  const [selectedTimeframe, setSelectedTimeframe] = useState<'5m' | '15m' | '1h' | '1D'>('5m');
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(false);
  const lastSeenChangeRef = useRef<number>(0);

  // Strategist state
  const [strategist, setStrategist] = useState<StrategistOutput | null>(null);
  const [strategistLoading, setStrategistLoading] = useState(false);
  const [strategistError, setStrategistError] = useState<string | null>(null);
  const lastStrategistCardRef = useRef<string | null>(null);

  // AI explain
  const { open: explainOpen, setOpen: setExplainOpen, param: explainParam, text: explainText, loading: explainLoading, explain } = useExplain();

  // Market hours dialog
  const [hoursOpen, setHoursOpen] = useState(false);

  // Fetch snapshot every 30s
  const fetchSnapshot = useCallback(async () => {
    if (!symbol) return;
    try {
      const data = await api.get<InstrumentSnapshotResponse>(`/api/instruments/${symbol}/snapshot`);
      setSnapshot(data);
      setSnapshotUnavailable(false);

      // Check for new material changes
      if (data.materialChanges && data.materialChanges.length > 0) {
        const latest = data.materialChanges[data.materialChanges.length - 1];
        if (latest.timestamp > lastSeenChangeRef.current) {
          lastSeenChangeRef.current = latest.timestamp;
          const variant = latest.severity === 'high' ? 'error' : 'info';
          toast(formatMaterialChange(latest), variant);
        }
      }
    } catch {
      // No cached snapshot (expired/never fetched) — stop showing skeletons
      setSnapshotUnavailable(true);
    } finally {
      setLoading(false);
    }
  }, [symbol, toast]);

  // Fetch decision every 5m
  const fetchDecision = useCallback(async () => {
    if (!symbol) return;
    try {
      const data = await api.get<DecisionResponse | null>(`/api/instruments/${symbol}/decision`);
      setDecision(data);
      // Clear stale strategist state when no active decision
      if (!data) {
        setStrategist(null);
        setStrategistError(null);
      }
    } catch {
      // Silent fail
    }
  }, [symbol]);

  // Fetch indicators every 5m
  const fetchIndicators = useCallback(async () => {
    if (!symbol) return;
    try {
      const data = await api.get<IndicatorsResponse>(`/api/instruments/${symbol}/indicators`);
      setIndicators(data);
    } catch {
      // Silent fail
    }
  }, [symbol]);

  // Fetch candles every 5m
  const fetchCandles = useCallback(async () => {
    if (!symbol) return;
    try {
      const data = await api.get<CandlesResponse>(`/api/instruments/${symbol}/candles?timeframe=${selectedTimeframe}`);
      setCandles(data.candles ?? []);
    } catch {
      // Silent fail
    }
  }, [symbol, selectedTimeframe]);

  const marketOpen = isMarketHours();
  useInterval(fetchSnapshot, marketOpen ? CADENCE.COCKPIT_HEADER_PRICE_MS : null);
  useInterval(fetchDecision, marketOpen ? CADENCE.DECISION_CARD_MS : null);
  useInterval(fetchIndicators, marketOpen ? CADENCE.COCKPIT_METRICS_MS : null);
  useInterval(fetchCandles, marketOpen ? CADENCE.DECISION_CARD_MS : null);

  // Re-fetch candles when timeframe changes (useInterval won't re-fire if delay is null)
  const prevTimeframeRef = useRef(selectedTimeframe);
  useEffect(() => {
    if (prevTimeframeRef.current !== selectedTimeframe) {
      prevTimeframeRef.current = selectedTimeframe;
      fetchCandles();
    }
  }, [selectedTimeframe, fetchCandles]);

  // Fetch strategist when decision card changes
  useEffect(() => {
    if (!decision?.id || !snapshot || !indicators) return;
    if (decision.id === lastStrategistCardRef.current) return;

    const payload = buildEngineDecisionPayload(decision, snapshot, indicators);
    if (!payload) return;

    lastStrategistCardRef.current = decision.id;

    const fetchStrategist = async () => {
      setStrategistLoading(true);
      setStrategistError(null);
      try {
        const result = await api.post<StrategistResponse>('/api/render/strategist', payload);
        setStrategist(result.strategist ?? null);
        if (result.rejection_reason) {
          setStrategistError(result.rejection_reason);
        }
      } catch {
        setStrategistError('Unable to fetch commentary');
      } finally {
        setStrategistLoading(false);
      }
    };
    fetchStrategist();
  }, [decision?.id, snapshot, indicators]);

  async function handleApprove() {
    if (!decision) return;
    setApproving(true);
    try {
      const res = await api.post(`/api/decisions/${decision.id}/approve`, {
        action: decision.recommendation,
        symbol,
      }) as { data?: { trade?: { success?: boolean; quantity?: number; ticker?: string; skippedReason?: string } } };
      const trade = res.data?.trade;
      if (trade?.success) {
        toast(`Trade executed: ${decision.recommendation.toUpperCase()} ${trade.quantity} ${trade.ticker ?? symbol}`, 'success');
      } else if (trade?.skippedReason) {
        toast(`Approved but trade skipped: ${trade.skippedReason}`, 'warning');
      } else {
        toast('Recommendation approved and logged.', 'success');
      }
      fetchDecision();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Unable to approve. Please try again.', 'error');
    } finally {
      setApproving(false);
    }
  }

  async function handleDismiss() {
    if (!decision) return;
    try {
      await api.post(`/api/decisions/${decision.id}/dismiss`, { symbol });
      toast('Recommendation dismissed.', 'info');
      fetchDecision();
    } catch {
      toast('Unable to dismiss. Please try again.', 'error');
    }
  }

  // Staleness: flag chart data older than ~15 min so a Friday-close chart on a
  // weekend is not mistaken for live data.
  const latestCandleTs = candles.length > 0 ? candles[candles.length - 1].timestamp : null;
  const candlesAreStale = latestCandleTs !== null && Date.now() - latestCandleTs > 15 * 60 * 1000;

  const riskScore = typeof decision?.riskScore === 'number' ? decision.riskScore : null;
  const riskLevel = riskScore !== null
    ? riskScore <= 30
      ? 'low'
      : riskScore <= 55
        ? 'moderate'
        : riskScore <= 75
          ? 'elevated'
          : 'high'
    : null;

  return (
    <Box>
      {/* Breadcrumb + Header */}
      <Box mb="6">
        <Box
          as="button"
          onClick={() => navigate('/watchlist')}
          fontSize="sm"
          color="text.brand"
          mb="2"
          display="flex"
          alignItems="center"
          gap="1"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M15 18L9 12L15 6" />
          </svg>
          Watchlist
        </Box>

        <Flex alignItems="baseline" gap="4">
          <Text fontSize="display-sm" color="text.primary">{symbol}</Text>
          {snapshot ? (
            <PriceDisplay
              price={snapshot.price}
              change={snapshot.change}
              changePercent={snapshot.changePercent}
              size="lg"
            />
          ) : snapshotUnavailable ? (
            <Text fontSize="sm" color="text.tertiary">
              No recent price data &mdash; updates resume when the market reopens
            </Text>
          ) : (
            <Skeleton width={200} height={30} />
          )}
        </Flex>

        <Flex gap="3" mt="2">
          {snapshot ? (
            <Dialog.Root open={hoursOpen} onOpenChange={(e) => setHoursOpen(e.open)}>
              <Dialog.Trigger asChild>
                <Box as="button" cursor="pointer" border="none" background="none" p="0">
                  <Badge variant={snapshot.marketStatus === 'open' ? 'success' : 'neutral'}>
                    {snapshot.marketStatus === 'open' ? 'Market open' : 'Market closed'}
                  </Badge>
                </Box>
              </Dialog.Trigger>
              <Dialog.Backdrop />
              <Dialog.Positioner>
                <Dialog.Content
                  bg="surface.primary"
                  border="1px solid"
                  borderColor="border.primary"
                  borderRadius="lg"
                  p="5"
                  maxW="360px"
                  w="90vw"
                >
                  {(() => {
                    const exchange = snapshot.exchange ?? '';
                    const schedule = getExchangeHours(exchange);
                    const hasExchange = !!exchange;
                    // Convert UTC "HH:MM" to user's local time
                    const utcToLocal = (hhmm: string) => {
                      const [h, m] = hhmm.split(':').map(Number);
                      const d = new Date();
                      d.setUTCHours(h, m, 0, 0);
                      return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
                    };
                    const localOpen = utcToLocal(schedule.openUtc);
                    const localClose = utcToLocal(schedule.closeUtc);
                    return (
                      <>
                        <Dialog.Title fontSize="16px" fontWeight="600" color="text.primary" mb="1">
                          {hasExchange ? schedule.label : 'Market'} Trading Hours
                        </Dialog.Title>
                        {hasExchange && (
                          <Text fontSize="xs" color="text.tertiary" m="0 0 3 0">
                            {symbol} trades on {schedule.label}
                          </Text>
                        )}
                        {!hasExchange && (
                          <Text fontSize="xs" color="text.tertiary" m="0 0 3 0">
                            Exchange not yet identified — showing cron window. Refresh to detect exchange.
                          </Text>
                        )}
                        <Flex direction="column" gap="3">
                          <Flex justify="space-between">
                            <Text fontSize="sm" color="text.secondary">Local time</Text>
                            <Text fontFamily="mono" fontSize="sm" color="text.primary" fontWeight="600">{localOpen} – {localClose}</Text>
                          </Flex>
                          <Flex justify="space-between">
                            <Text fontSize="sm" color="text.secondary">UTC</Text>
                            <Text fontFamily="mono" fontSize="sm" color="text.tertiary">{schedule.openUtc} – {schedule.closeUtc}</Text>
                          </Flex>
                          <Flex justify="space-between">
                            <Text fontSize="sm" color="text.secondary">Exchange timezone</Text>
                            <Text fontSize="sm" color="text.primary">{schedule.timezone}</Text>
                          </Flex>
                          <Flex justify="space-between">
                            <Text fontSize="sm" color="text.secondary">Trading days</Text>
                            <Text fontSize="sm" color="text.primary">Mon – Fri</Text>
                          </Flex>
                          <Flex justify="space-between">
                            <Text fontSize="sm" color="text.secondary">Status</Text>
                            <Badge variant={snapshot.marketStatus === 'open' ? 'success' : 'neutral'}>
                              {snapshot.marketStatus === 'open' ? 'Open now' : 'Closed'}
                            </Badge>
                          </Flex>
                        </Flex>
                      </>
                    );
                  })()}
                  <Dialog.CloseTrigger position="absolute" top="3" right="3" />
                </Dialog.Content>
              </Dialog.Positioner>
            </Dialog.Root>
          ) : snapshotUnavailable ? (
            // No snapshot to read exchange hours from — fall back to the cron
            // window truth so the badge is still honest, never a skeleton.
            <Badge variant={marketOpen ? 'success' : 'neutral'}>
              {marketOpen ? 'Market open' : 'Market closed'}
            </Badge>
          ) : (
            <Skeleton width={100} height={24} borderRadius="full" />
          )}
        </Flex>
      </Box>

      {/* Two-column layout */}
      <Grid
        templateColumns={{ base: '1fr', lg: '1fr 340px' }}
        gap="6"
        alignItems="start"
      >
        {/* Left column: Chart + Metrics + Decision Card */}
        <Flex direction="column" gap="4">
          <Card minHeight={400}>
            <CardHeader>
              <Flex gap="1" align="center" justify="space-between" flexWrap="wrap">
                <Flex gap="1">
                {(['5m', '15m', '1h', '1D'] as const).map((tf) => (
                  <Box
                    key={tf}
                    as="button"
                    onClick={() => {
                      if (tf !== selectedTimeframe) {
                        setSelectedTimeframe(tf);
                        setCandles([]);
                      }
                    }}
                    px="3"
                    py="1"
                    borderRadius="sm"
                    fontSize="12px"
                    fontWeight="500"
                    cursor="pointer"
                    border="none"
                    color={tf === selectedTimeframe ? 'text.brand' : 'text.tertiary'}
                    bg={tf === selectedTimeframe ? 'brand.50' : 'transparent'}
                  >
                    {tf}
                  </Box>
                ))}
                </Flex>
                {candlesAreStale && latestCandleTs !== null && (
                  <Text fontSize="12px" color="text.tertiary" fontFamily="mono">
                    as of {formatAsOf(latestCandleTs)}
                  </Text>
                )}
              </Flex>
            </CardHeader>
            <CardBody>
              {candles.length > 0 ? (
                <CandlestickChart candles={candles} height={350} />
              ) : (
                <Flex
                  height="350px"
                  alignItems="center"
                  justifyContent="center"
                  bg="bg.secondary"
                  borderRadius="md"
                  color="text.tertiary"
                  fontSize="14px"
                >
                  {loading ? <Skeleton width="100%" height="100%" /> : 'No candle data available'}
                </Flex>
              )}
            </CardBody>
          </Card>

          {/* Key Metrics Strip */}
          <Card variant="compact">
            <SimpleGrid
              columns={{ base: 2, md: 4 }}
              gap="4"
            >
              {/* NOTE: "Spread" was removed — nothing in the worker ever produced it
                  (the snapshot route has no spread field), so it was permanently "--". */}
              {[
                { key: 'dayHighLow', label: 'Day high / low', value: snapshot ? `${snapshot.dayHigh.toFixed(2)} / ${snapshot.dayLow.toFixed(2)}` : '--' },
                { key: 'week52HighLow', label: '52-week high / low', value: snapshot?.week52High ? `${snapshot.week52High.toFixed(2)} / ${snapshot.week52Low?.toFixed(2)}` : '--' },
                { key: 'marketCap', label: 'Market cap', value: snapshot?.marketCap ? formatMarketCap(snapshot.marketCap) : '--' },
                { key: 'peRatio', label: 'P/E ratio', value: snapshot?.peRatio?.toFixed(1) ?? '--' },
              ].map((metric) => (
                <Box
                  key={metric.label}
                  onClick={() => metric.value !== '--' && explain(metric.key, metric.label, metric.value, 'Instrument Metrics')}
                  cursor={metric.value !== '--' ? 'pointer' : 'default'}
                >
                  <Text fontSize="xs" color="text.tertiary" mb="0.5">
                    {metric.label}
                    {metric.value !== '--' && <Text as="span" ml="1" opacity="0.5">?</Text>}
                  </Text>
                  <Text fontFamily="mono" fontSize="sm" color="text.primary">{metric.value}</Text>
                </Box>
              ))}
            </SimpleGrid>
          </Card>

          {/* Decision Card - below chart in main column */}
          <Card
            variant="elevated"
            accentColor={
              decision?.recommendation === 'buy' ? 'var(--chakra-colors-brand-500, #2970ff)'
                : decision?.recommendation === 'reduce' ? 'var(--chakra-colors-warning-500, #f79009)'
                : decision?.recommendation === 'exit' ? 'var(--chakra-colors-error-500, #f04438)'
                : 'var(--chakra-colors-border-primary, #d0d5dd)'
            }
          >
            <CardHeader>
              <Flex alignItems="center" gap="2">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--chakra-colors-text-brand, #2970ff)" strokeWidth="2" strokeLinecap="round">
                  <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
                </svg>
                <Text fontSize="lg" color="text.primary">Current Recommendation</Text>
              </Flex>
            </CardHeader>
            <CardBody>
              {decision && decision.status === 'active' ? (
                <Flex direction="column" gap="4">
                  {!marketOpen && (
                    <Text fontSize="xs" color="text.tertiary" m={0}>
                      Computed at {formatAsOf(decision.computedAt)} &mdash; market closed
                    </Text>
                  )}
                  <Flex alignItems="center" gap="3">
                    <RecommendationBadge recommendation={decision.recommendation} />
                    <Badge variant="neutral">{decision.confidence} confidence</Badge>
                  </Flex>

                  <Text fontSize="md" color="text.secondary">
                    {decision.rationale}
                  </Text>

                  <Grid templateColumns="1fr 1fr" gap="3">
                    {decision.suggestedLevels.entryZone && (
                      <DataRow
                        label="Entry zone"
                        value={`${decision.suggestedLevels.entryZone.low.toFixed(2)} -- ${decision.suggestedLevels.entryZone.high.toFixed(2)}`}
                      />
                    )}
                    <DataRow label="Stop loss" value={decision.suggestedLevels.stopLoss.toFixed(2)} />
                    <DataRow label="Target 1" value={decision.suggestedLevels.takeProfit1.toFixed(2)} />
                    {decision.suggestedLevels.takeProfit2 && (
                      <DataRow label="Target 2" value={decision.suggestedLevels.takeProfit2.toFixed(2)} />
                    )}
                  </Grid>

                  <Flex gap="3" mt="2">
                    {decision.recommendation !== 'hold' && (
                      <Button onClick={handleApprove} loading={approving} flex="1">
                        Approve {decision.recommendation.charAt(0).toUpperCase() + decision.recommendation.slice(1)}
                      </Button>
                    )}
                    <Button variant="ghost" onClick={handleDismiss} flex={decision.recommendation === 'hold' ? '1' : undefined}>
                      Dismiss
                    </Button>
                  </Flex>

                  <Text fontSize="xs" color="text.tertiary">
                    Approving will place a market order on your Trading 212 demo account.
                  </Text>
                </Flex>
              ) : (
                <Box textAlign="center" py="6">
                  <Text fontSize="md" color="text.primary" mb="2">
                    No recommendation yet
                  </Text>
                  <Text fontSize="sm" color="text.tertiary">
                    A recommendation will appear once the analysis engine has enough data.
                  </Text>
                </Box>
              )}
            </CardBody>
          </Card>
        </Flex>

        {/* Right column - sticky sidebar */}
        <Flex
          direction="column"
          gap="4"
          position="sticky"
          top="4"
        >
          {/* Risk Summary */}
          <Card>
            <CardHeader>
              <Text fontSize="lg" color="text.primary">Risk Summary</Text>
            </CardHeader>
            <CardBody>
              {riskLevel !== null ? (
                <Flex direction="column" gap="3">
                  <Flex justify="space-between" alignItems="center">
                    <Text fontSize="sm" color="text.secondary">Risk level</Text>
                    <RiskBadge level={riskLevel} />
                  </Flex>
                  <Box>
                    <Flex justify="space-between" mb="1">
                      <Text fontSize="sm" color="text.secondary">Risk score</Text>
                      <Text fontFamily="mono" fontSize="sm">{riskScore}/100</Text>
                    </Flex>
                    <Box
                      height="8px"
                      bg="bg.tertiary"
                      borderRadius="full"
                      overflow="hidden"
                    >
                      <Box
                        height="100%"
                        width={`${riskScore}%`}
                        borderRadius="full"
                        bg={riskLevel === 'low' ? 'status.success.text'
                          : riskLevel === 'moderate' ? 'warning.500'
                          : riskLevel === 'elevated' ? 'warning.500'
                          : 'status.error.text'}
                        transition="width 300ms ease-out"
                      />
                    </Box>
                  </Box>
                  {indicators && (
                    <>
                      <DataRow label="SMA 20" value={indicators.sma20.toFixed(2)} onClick={() => explain('sma20', 'SMA 20', indicators.sma20.toFixed(2), 'Technical Indicators')} />
                      <DataRow label="SMA 50" value={indicators.sma50.toFixed(2)} onClick={() => explain('sma50', 'SMA 50', indicators.sma50.toFixed(2), 'Technical Indicators')} />
                      <DataRow label="RSI (14)" value={indicators.rsi14.toFixed(1)} onClick={() => explain('rsi14', 'RSI (14)', indicators.rsi14.toFixed(1), 'Technical Indicators')} />
                      <DataRow label="Regime" value={indicators.regime?.replace('_', ' ') ?? '--'} onClick={() => explain('regime', 'Regime', indicators.regime?.replace('_', ' ') ?? '--', 'Technical Indicators')} />
                    </>
                  )}
                </Flex>
              ) : loading ? (
                <Flex direction="column" gap="3">
                  {Array.from({ length: 5 }, (_, i) => (
                    <Skeleton key={i} height={20} />
                  ))}
                </Flex>
              ) : (
                <Box textAlign="center" py="4">
                  <Text fontSize="sm" color="text.tertiary">
                    Waiting for enough data to compute risk metrics.
                  </Text>
                </Box>
              )}
            </CardBody>
          </Card>

          {/* Trade-off Lens */}
          <TradeOffLens
            riskScore={riskScore}
            riskBand={decision?.riskBand ?? riskLevel}
            opportunityScore={decision?.opportunityScore ?? null}
            riskDrivers={(decision?.riskDrivers as RiskDriver[]) ?? []}
            strategist={strategist}
            strategistLoading={strategistLoading}
            strategistError={strategistError}
          />

        </Flex>
      </Grid>

      <ExplainDialog
        open={explainOpen}
        onOpenChange={setExplainOpen}
        label={explainParam?.label ?? ''}
        value={explainParam?.value ?? ''}
        category={explainParam?.category ?? ''}
        explanation={explainText}
        loading={explainLoading}
      />
    </Box>
  );
}

function DataRow({ label, value, onClick }: { label: string; value: string; onClick?: () => void }) {
  return (
    <Flex
      justify="space-between"
      alignItems="baseline"
      cursor={onClick ? 'pointer' : 'default'}
      onClick={onClick}
    >
      <Text fontSize="sm" color="text.secondary">
        {label}
        {onClick && <Text as="span" ml="1" opacity="0.5" fontSize="11px">?</Text>}
      </Text>
      <Text fontFamily="mono" fontSize="sm" color="text.primary">{value}</Text>
    </Flex>
  );
}

/** "Fri 20:55 UTC" for this week's data; adds the date once it's older. */
function formatAsOf(ts: number): string {
  const d = new Date(ts);
  const day = d.toLocaleDateString('en-GB', { weekday: 'short', timeZone: 'UTC' });
  const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' });
  const ageDays = (Date.now() - ts) / 86_400_000;
  if (ageDays > 6) {
    const date = d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });
    return `${date} ${time} UTC`;
  }
  return `${day} ${time} UTC`;
}

function formatMaterialChange(change: MaterialChange): string {
  const pct = change.details.percentChange?.toFixed(1) ?? '?';
  switch (change.type) {
    case 'price_threshold_breach':
      return `Price moved ${pct}% from decision level`;
    case 'volatility_spike':
      return `Volatility spike detected (ATR up ${pct}%)`;
    case 'volume_spike':
      return `Volume spike: ${pct}% above average`;
    default:
      return 'Material change detected';
  }
}

function formatMarketCap(value: number): string {
  if (value >= 1e12) return `$${(value / 1e12).toFixed(1)}T`;
  if (value >= 1e9) return `$${(value / 1e9).toFixed(1)}B`;
  if (value >= 1e6) return `$${(value / 1e6).toFixed(1)}M`;
  return `$${value.toLocaleString()}`;
}

function buildEngineDecisionPayload(
  decision: DecisionResponse,
  snapshot: InstrumentSnapshotResponse,
  indicators: IndicatorsResponse | null,
): Record<string, unknown> | null {
  if (!indicators) return null;

  return {
    symbol: decision.symbol,
    decision_card_id: decision.id,
    timestamp: decision.computedAt,
    regime: indicators.regime ?? 'dead',
    regime_strength: indicators.regimeStrength ?? 0,
    risk_score: decision.riskScore,
    risk_band: decision.riskBand ?? 'moderate',
    risk_components: {},
    risk_drivers: decision.riskDrivers ?? [],
    opportunity_score: decision.opportunityScore ?? 50,
    opportunity_components: { trend_clarity: 0, liquidity_quality: 0, noise_suitability: 0, extension_score: 0 },
    recommendation: decision.recommendation,
    confidence: decision.confidence,
    rationale: decision.rationale,
    suggested_levels: decision.suggestedLevels,
    price_at_computation: snapshot.price,
    allowed_actions: decision.allowedActions ?? ['observe', 'hold'],
    triggers: [],
    metrics: {
      rsi14: indicators.rsi14,
      adx14: indicators.adx14,
      atr14: indicators.atr14,
      atr_pct: snapshot.price > 0 ? indicators.atr14 / snapshot.price : 0,
      sma20_dist: 0,
      ema_dist: 0,
      spread_norm: 0,
      vol_ratio: 1,
    },
    has_position: false,
  };
}
